// types/templates.js - Типы данных для шаблонов и покупок

// ============================================================================= 
// ТИПЫ ШАБЛОНОВ
// ============================================================================= 

/** 
 * Типы доступных договоров
 */
export const CONTRACT_TYPES = {
  RENT: 'rent',
  LOAN: 'loan'
};

/**
 * Статусы покупки шаблона
 */
export const PURCHASE_STATUS = {
  NOT_PURCHASED: 'not_purchased',
  PURCHASED: 'purchased',
  EXPIRED: 'expired'
};

/**
 * Статусы платежа
 */
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  SUCCESS: 'success',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
};

// ============================================================================= 
// МОДЕЛИ ДАННЫХ
// ============================================================================= 

/**
 * Модель шаблона договора
 */
export const ContractTemplateModel = {
  id: '',                    // string - уникальный ID шаблона
  type: '',                  // CONTRACT_TYPES - тип договора
  name: '',                  // string - название шаблона
  description: '',           // string - описание шаблона
  price: 0,                  // number - цена в рублях
  duration: 7,               // number - срок действия в днях
  features: [],              // string[] - список особенностей
  icon: '',                  // string - иконка (emoji или путь к файлу)
  previewFields: [],         // object[] - поля для превью документа
  isPopular: false,          // boolean - популярный шаблон
  createdAt: ''              // string (ISO date) - дата создания
};

/**
 * Модель покупки шаблона
 */
export const PurchaseModel = {
  id: '',                    // string - уникальный ID покупки
  userId: '',                // string - ID пользователя
  templateId: '',            // string - ID шаблона
  templateType: '',          // CONTRACT_TYPES - тип договора
  price: 0,                  // number - цена покупки
  purchaseDate: '',          // string (ISO date) - дата покупки
  expiryDate: '',            // string (ISO date) - дата истечения
  status: '',                // PURCHASE_STATUS - статус покупки
  paymentId: '',             // string - ID платежа
  paymentStatus: ''          // PAYMENT_STATUS - статус платежа
};

/**
 * Модель платежа (для YooMoney заглушки)
 */
export const PaymentModel = {
  id: '',                    // string - уникальный ID платежа
  amount: 0,                 // number - сумма платежа
  currency: 'RUB',           // string - валюта
  description: '',           // string - описание платежа
  status: '',                // PAYMENT_STATUS - статус
  paymentMethod: 'yoomoney', // string - метод оплаты
  confirmationUrl: '',       // string - URL для подтверждения
  createdAt: '',             // string (ISO date) - дата создания
  confirmedAt: ''            // string (ISO date) - дата подтверждения
};

/**
 * Модель пользовательского договора (расширенная)
 */
export const UserContractModel = {
  id: '',                    // string - уникальный ID договора
  userId: '',                // string - ID пользователя
  templateId: '',            // string - ID шаблона
  templateType: '',          // CONTRACT_TYPES - тип договора
  data: {},                  // object - данные договора
  isPurchased: false,        // boolean - оплачен ли доступ к шаблону
  purchaseId: '',            // string - ID покупки (если есть)
  createdAt: '',             // string (ISO date) - дата создания
  lastModified: '',          // string (ISO date) - последнее изменение
  downloadCount: 0           // number - количество скачиваний
};

// ============================================================================= 
// КОНСТАНТЫ
// ============================================================================= 

/**
 * Настройки шаблонов
 */
export const TEMPLATE_CONSTANTS = {
  DEFAULT_DURATION: 7,       // дней
  MAX_DOWNLOADS: -1,         // -1 = неограниченно
  PREVIEW_HEIGHT: 280,       // px для превью документа
  PAYMENT_TIMEOUT: 300000,   // 5 минут в миллисекундах
};

/**
 * Сообщения для пользователя
 */
export const TEMPLATE_MESSAGES = {
  PURCHASE_SUCCESS: 'Шаблон успешно куплен! Теперь вы можете создавать договоры.',
  PURCHASE_FAILED: 'Ошибка при покупке шаблона. Попробуйте еще раз.',
  PAYMENT_CANCELLED: 'Платеж был отменен.',
  TEMPLATE_EXPIRED: 'Срок действия шаблона истек. Продлите подписку.',
  LOGIN_REQUIRED: 'Для скачивания договора необходимо войти в систему.'
};

// ============================================================================= 
// ВАЛИДАЦИЯ
// ============================================================================= 

/**
 * Валидация данных шаблона
 */
export const validateTemplate = (template) => {
  const errors = {};

  if (!template.name?.trim()) {
    errors.name = 'Название шаблона обязательно';
  }

  if (!template.type || !Object.values(CONTRACT_TYPES).includes(template.type)) {
    errors.type = 'Некорректный тип договора';
  }

  if (!template.price || template.price <= 0) {
    errors.price = 'Цена должна быть больше 0';
  }

  if (!template.duration || template.duration <= 0) {
    errors.duration = 'Срок действия должен быть больше 0';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Проверка истечения срока покупки
 */
export const isPurchaseExpired = (purchase) => {
  if (!purchase || !purchase.expiryDate) return true;
  return new Date(purchase.expiryDate) < new Date();
};

/**
 * Проверка возможности скачивания
 */
export const canDownloadContract = (contract, purchase = null) => {
  // Если нет покупки - нельзя скачивать
  if (!purchase) return false;
  
  // Если покупка истекла - нельзя скачивать
  if (isPurchaseExpired(purchase)) return false;
  
  // Если статус покупки не "purchased" - нельзя скачивать
  if (purchase.status !== PURCHASE_STATUS.PURCHASED) return false;
  
  return true;
};

// ============================================================================= 
// ЭКСПОРТ
// ============================================================================= 

export default {
  CONTRACT_TYPES,
  PURCHASE_STATUS,
  PAYMENT_STATUS,
  ContractTemplateModel,
  PurchaseModel,
  PaymentModel,
  UserContractModel,
  TEMPLATE_CONSTANTS,
  TEMPLATE_MESSAGES,
  validateTemplate,
  isPurchaseExpired,
  canDownloadContract
};